import type { ActivityCounts, TeamActivityMember } from '@/api/gamma-team/types';
import { SortDirection, type SortDirectionType } from '@/api/types';

export type TeamSortKey = 'name' | keyof ActivityCounts;

export interface TeamSort {
  key: TeamSortKey;
  direction: SortDirectionType;
}

export const DEFAULT_TEAM_SORT: TeamSort = {
  key: 'contactsReached',
  direction: SortDirection.DESC,
};

export const UNNAMED_MEMBER = 'Unknown member';

export const memberName = (member: Pick<TeamActivityMember, 'name'>): string =>
  member.name?.trim() || UNNAMED_MEMBER;

export const nextTeamSort = (current: TeamSort, key: TeamSortKey): TeamSort => {
  if (current.key === key) {
    return {
      key,
      direction: current.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC,
    };
  }
  return { key, direction: key === 'name' ? SortDirection.ASC : SortDirection.DESC };
};

const compareBy = (a: TeamActivityMember, b: TeamActivityMember, key: TeamSortKey): number =>
  key === 'name' ? memberName(a).localeCompare(memberName(b)) : a[key] - b[key];

export const sortTeamMembers = (
  members: readonly TeamActivityMember[],
  sort: TeamSort,
): TeamActivityMember[] =>
  [...members].sort((a, b) => {
    const inactive = (member: TeamActivityMember) =>
      member.entries === 0 && member.dealsCreated === 0 && member.dealsAdvanced === 0;
    const idle = Number(inactive(a)) - Number(inactive(b));
    if (idle !== 0) return idle;
    const primary = compareBy(a, b, sort.key);
    if (primary !== 0) return sort.direction === SortDirection.ASC ? primary : -primary;
    return (
      b.contactsReached - a.contactsReached ||
      b.entries - a.entries ||
      memberName(a).localeCompare(memberName(b))
    );
  });
